import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Box, Button, Card, Divider, Stack, Tab, Tabs, Typography } from '@mui/material';
import { PollRounded, AddRounded, AutoAwesomeRounded } from '@mui/icons-material';
import { useTenantStore } from '../../stores/tenant.store';
import { SectionPicker } from './components/SectionPicker';
import { surveyApi } from '../../lib/api/survey.api';
import { SurveyManagementPanel } from './components/SurveyManagementPanel';
import { SurveyAutomationCard } from './components/SurveyAutomationCard';
import { SurveyTemplateEditorDialog } from './components/SurveyTemplateEditorDialog';
import type { SurveyTemplateDto } from '../../lib/api/survey.api';
import { errorText } from '../../lib/error-text';

type SurveyTab = 'surveys' | 'automation' | 'templates';

export function SurveysAdminPage(): JSX.Element {
  const { t } = useTranslation('sis');
  const { currentTenantId } = useTenantStore();
  const queryClient = useQueryClient();

  const [tab, setTab] = useState<SurveyTab>('surveys');
  const [automationSectionId, setAutomationSectionId] = useState('');
  const [editorTemplate, setEditorTemplate] = useState<SurveyTemplateDto | null | undefined>(
    undefined,
  ); // undefined = closed, null = create, dto = edit
  const [feedback, setFeedback] = useState<{ kind: 'success' | 'error'; msg: string } | null>(null);

  const templatesQ = useQuery({
    queryKey: ['survey-templates', currentTenantId],
    queryFn: () => surveyApi.listTemplates({ tenantId: currentTenantId! }),
    enabled: !!currentTenantId,
  });
  const templates = templatesQ.data?.templates ?? [];

  const seedMutation = useMutation({
    mutationFn: () => surveyApi.seedStandardTemplates({ tenantId: currentTenantId! }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['survey-templates', currentTenantId] });
      setFeedback({ kind: 'success', msg: t('surveys.seedDone') });
    },
    onError: (e) => setFeedback({ kind: 'error', msg: errorText(e, 'error') }),
  });

  const archiveTemplateMutation = useMutation({
    mutationFn: (templateId: string) =>
      surveyApi.archiveTemplate({ tenantId: currentTenantId!, templateId }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['survey-templates', currentTenantId] }),
  });

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 0.5 }}>
        <PollRounded color="primary" />
        <Typography variant="h4" fontWeight={700}>
          {t('surveys.title')}
        </Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('surveys.subtitle')}
      </Typography>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
        <Tab value="surveys" label={t('surveys.tabSurveys')} />
        <Tab value="automation" label={t('surveys.tabAutomation')} />
        <Tab value="templates" label={t('surveys.tabTemplates')} />
      </Tabs>

      {tab === 'surveys' && <SurveyManagementPanel />}

      {tab === 'automation' && (
        <Stack spacing={2} sx={{ maxWidth: 760 }}>
          <Alert severity="info" icon={<AutoAwesomeRounded />}>
            {t('surveys.automation.intro')}
          </Alert>
          <SectionPicker value={automationSectionId} onChange={setAutomationSectionId} />
          {automationSectionId ? (
            <SurveyAutomationCard sectionId={automationSectionId} templates={templates} />
          ) : (
            <Typography color="text.secondary">{t('surveys.automation.pickSection')}</Typography>
          )}
        </Stack>
      )}

      {tab === 'templates' && (
        <Box sx={{ maxWidth: 760 }}>
          {feedback && (
            <Alert severity={feedback.kind} sx={{ mb: 2 }} onClose={() => setFeedback(null)}>
              {feedback.msg}
            </Alert>
          )}
          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            <Button
              variant="contained"
              startIcon={<AddRounded />}
              onClick={() => setEditorTemplate(null)}
            >
              {t('surveys.tplNew')}
            </Button>
            {templates.length === 0 && (
              <Button
                variant="outlined"
                disabled={seedMutation.isPending}
                onClick={() => seedMutation.mutate()}
              >
                {seedMutation.isPending ? t('surveys.seeding') : t('surveys.seedTemplates')}
              </Button>
            )}
          </Stack>
          {templates.length === 0 ? (
            <Typography color="text.secondary">{t('surveys.noTemplates')}</Typography>
          ) : (
            <Card variant="outlined" sx={{ borderRadius: 3 }}>
              <Stack divider={<Divider flexItem />} sx={{ p: 1 }}>
                {templates.map((tpl) => (
                  <Stack
                    key={tpl.id}
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{ py: 1, px: 1 }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Typography fontWeight={600}>{tpl.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {tpl.questions.length} {t('surveys.questions')}
                        {tpl.isStandard ? ` · ${t('surveys.tplStandard')}` : ''}
                      </Typography>
                    </Box>
                    <Button size="small" onClick={() => setEditorTemplate(tpl)}>
                      {t('surveys.tplEdit')}
                    </Button>
                    <Button
                      size="small"
                      color="error"
                      disabled={archiveTemplateMutation.isPending}
                      onClick={() => archiveTemplateMutation.mutate(tpl.id)}
                    >
                      {t('surveys.tplArchive')}
                    </Button>
                  </Stack>
                ))}
              </Stack>
            </Card>
          )}
          {editorTemplate !== undefined && (
            <SurveyTemplateEditorDialog
              {...(editorTemplate ? { template: editorTemplate } : {})}
              onClose={() => setEditorTemplate(undefined)}
            />
          )}
        </Box>
      )}
    </Box>
  );
}
